import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * MultiUploadDropZone — global drag-and-drop surface that turns any page
 * into a drop target for up to 20 files at once.
 *
 * The component mounts a full-viewport invisible listener that:
 *   1. Detects when a drag session enters the window (with files on the
 *      DataTransfer). Non-file drags (e.g. text selections) are ignored so
 *      we never hijack the usual app interactions.
 *   2. Shows an overlay prompting the user to drop, labelled with the target
 *      collection when one is provided.
 *   3. On drop, forwards the files to the provided uploader and surfaces
 *      progress as a sonner toast with image previews (see
 *      `multi-upload-toast.tsx`) instead of a dedicated status panel.
 *
 * This package stays free of routing/API dependencies by accepting the
 * uploader as a prop — the host app (viewer) wires that up.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useMultiFileUpload, MAX_UPLOADS } from "./use-multi-file-upload";
import { MultiUploadToastBody } from "./multi-upload-toast";
import "./multi-upload-drop-zone.scss";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Class on the sonner toast `<li>` so we can target the upload pill in CSS. */
const UPLOAD_TOAST_CLASSNAME = "viewer-upload-toast-pill";
/** How long the "done" state lingers before the toast auto-dismisses. */
const DONE_DISMISS_MS = 1800;
/** How long a fully-failed batch lingers so the user can read the message. */
const FAILURE_DISMISS_MS = 3200;
// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
/**
 * Returns true iff the current drag session carries file data. Using the
 * `types` list (which is present during dragover) is critical — `items`
 * is empty until drop in many browsers during a drag-from-OS session.
 */
function dragHasFiles(event) {
    const types = event.dataTransfer?.types;
    if (!types)
        return false;
    for (let i = 0; i < types.length; i++) {
        if (types[i] === "Files")
            return true;
    }
    return false;
}
/**
 * Reconcile a Map<uploadId, ObjectURL> with the current uploads list:
 *   - Create URLs for any new image uploads.
 *   - Revoke URLs for uploads that have been cleared from the batch.
 *
 * Videos are intentionally skipped — the toast renders an icon placeholder
 * for them, so creating an object URL would just leak memory.
 */
function reconcilePreviewUrls(cache, uploads) {
    const next = new Map();
    const seen = new Set();
    for (const upload of uploads) {
        seen.add(upload.id);
        if (upload.file.type.startsWith("image/") === false)
            continue;
        const existing = cache.get(upload.id);
        if (existing !== undefined) {
            next.set(upload.id, existing);
            continue;
        }
        next.set(upload.id, URL.createObjectURL(upload.file));
    }
    for (const [id, url] of cache) {
        if (seen.has(id) === false)
            URL.revokeObjectURL(url);
    }
    return next;
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const MultiUploadDropZone = ({ uploadFile, collectionId, targetLabel, acceptMimePrefixes, onFileComplete, onBatchComplete, }) => {
    const [isDragging, setIsDragging] = useState(false);
    // Windows fires enter/leave rapidly as the cursor crosses child element
    // boundaries. Counting enters/leaves keeps the overlay visible until the
    // drag session actually exits the viewport.
    const dragDepthRef = useRef(0);
    const { uploads, isUploading, uploadFiles, clear } = useMultiFileUpload({
        uploadFile,
        ...(acceptMimePrefixes !== undefined ? { acceptMimePrefixes } : {}),
        ...(onFileComplete !== undefined ? { onFileComplete } : {}),
        onBatchComplete: (summary) => {
            onBatchComplete?.(summary);
            // Leave the toast up for a moment so the user sees completion, then
            // auto-dismiss via the sonner `duration`. We also clear the staged
            // batch so the next drop starts fresh (this runs *after* the pause
            // so the completion toast has time to render).
            const dismiss = summary.failed === summary.total ? FAILURE_DISMISS_MS : DONE_DISMISS_MS;
            window.setTimeout(() => clear(), dismiss + 200);
        },
    });
    // --- global drag listeners ---------------------------------------------
    useEffect(() => {
        const onDragEnter = (e) => {
            if (dragHasFiles(e) === false)
                return;
            dragDepthRef.current += 1;
            if (dragDepthRef.current === 1)
                setIsDragging(true);
        };
        const onDragLeave = (e) => {
            if (dragHasFiles(e) === false)
                return;
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0)
                setIsDragging(false);
        };
        const onDragOver = (e) => {
            if (dragHasFiles(e) === false)
                return;
            // Required to allow drop.
            e.preventDefault();
            if (e.dataTransfer)
                e.dataTransfer.dropEffect = "copy";
        };
        const onDrop = (e) => {
            if (dragHasFiles(e) === false)
                return;
            e.preventDefault();
            dragDepthRef.current = 0;
            setIsDragging(false);
            const files = e.dataTransfer?.files;
            if (!files || files.length === 0)
                return;
            void uploadFiles(files, {
                ...(collectionId !== undefined ? { collectionId } : {}),
            });
        };
        window.addEventListener("dragenter", onDragEnter);
        window.addEventListener("dragleave", onDragLeave);
        window.addEventListener("dragover", onDragOver);
        window.addEventListener("drop", onDrop);
        return () => {
            window.removeEventListener("dragenter", onDragEnter);
            window.removeEventListener("dragleave", onDragLeave);
            window.removeEventListener("dragover", onDragOver);
            window.removeEventListener("drop", onDrop);
        };
    }, [uploadFiles, collectionId]);
    // --- sonner toast (progress pill) ---------------------------------------
    // Preview URLs outlive any individual React render — we keep them in a
    // ref so we only create an object URL the first time a given file is
    // staged, and revoke it once the file leaves the batch.
    const previewUrlsRef = useRef(new Map());
    // Stable toast id so repeated `toast.custom(..., { id })` calls replace
    // the same pill instead of stacking new ones.
    const toastIdRef = useRef(undefined);
    useEffect(() => {
        if (uploads.length === 0) {
            // Batch cleared — revoke any lingering URLs and drop the id so the
            // next drop mints a fresh toast.
            for (const url of previewUrlsRef.current.values())
                URL.revokeObjectURL(url);
            previewUrlsRef.current = new Map();
            toastIdRef.current = undefined;
            return;
        }
        previewUrlsRef.current = reconcilePreviewUrls(previewUrlsRef.current, uploads);
        const allFailed = isUploading === false && uploads.every((u) => u.status === "error");
        const duration = isUploading
            ? Infinity
            : allFailed
                ? FAILURE_DISMISS_MS
                : DONE_DISMISS_MS;
        const id = toast.custom(() => (_jsx(MultiUploadToastBody, { uploads: uploads, previewUrls: previewUrlsRef.current, isUploading: isUploading })), {
            ...(toastIdRef.current !== undefined ? { id: toastIdRef.current } : {}),
            duration,
            className: UPLOAD_TOAST_CLASSNAME,
        });
        toastIdRef.current = id;
    }, [uploads, isUploading]);
    // Final safety net: revoke any surviving URLs if the host unmounts while
    // uploads are staged.
    useEffect(() => {
        return () => {
            for (const url of previewUrlsRef.current.values())
                URL.revokeObjectURL(url);
            previewUrlsRef.current = new Map();
        };
    }, []);
    // --- render -------------------------------------------------------------
    const heading = (() => {
        if (targetLabel)
            return `Drop to add to ${targetLabel}`;
        if (collectionId)
            return "Drop to add to this collection";
        return "Drop to capture";
    })();
    const subheading = `Up to ${MAX_UPLOADS} files at once`;
    const showOverlay = isDragging;
    return (_jsx(_Fragment, { children: showOverlay ? (_jsx("div", { className: "multi-upload-drop-zone__overlay", role: "presentation", children: _jsxs("div", { className: "multi-upload-drop-zone__overlay-panel", children: [_jsx("div", { className: "multi-upload-drop-zone__overlay-title", children: heading }), _jsx("div", { className: "multi-upload-drop-zone__overlay-sub", children: subheading })] }) })) : undefined }));
};
